
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import CombinationsConfiguratorCanvas from '../combinations/CombinationsConfiguratorCanvas.vue';
import { WindowTemplate } from '@/interfaces/components/combinations/WindowTemplate';
import { WindowElement } from '@/interfaces/components/combinations/WindowElement';
import { CombinationsConfiguratorFormData } from '@/interfaces/components/combinations/CombinationsConfiguratorFormData';
import { GlobalOptions } from '@/enums/global/GlobalOptions';
import CombinationsWarning from '@/components/global/combinations/CombinationsWarning.vue';

@Component({
    name: 'CombinationsConfiguratorTemplate',
    components: { CombinationsConfiguratorCanvas, CombinationsWarning },
})
export default class CombinationsConfiguratorTemplate extends Vue {
    @Prop({ required: true }) private productFormData!: CombinationsConfiguratorFormData[] | null;
    @Prop({ required: true }) private selectedRows!: number[];
    @Prop({ type: String, required: true }) private guideType!: string;
    @Prop({ type: String, required: true }) private secondGuideType!: string;
    @Prop({ type: String, required: true }) private combinationType!: string;
    @Prop({ type: Boolean, required: true }) private isLastStep!: boolean;
    @Prop({ type: Number, required: true }) private widthDifference!: number;
    @Prop({ type: Number, required: true }) private combinedWidth!: number;
    @Prop({ type: Number, required: true }) private currentStep!: number;
    @Prop({ type: Object, required: true }) private currentConfig!: any;
    @Prop({ type: Object, required: true }) private dynamicCanvasSizesBasedOnProduct!: {
        leftGuide: string;
        rightGuide: string;
        centerGuide: string;
        extraMarginForCanvasStage: number;
    };

    private extraSpaceForLabels = 400;
    private percentageIncreasement = 2.5;
    private dividerForScaleAdjustmentBasis = 0.2; // Less value means less space between windows
    private extraWidthPaddingScale = 0.3;
    private selectedTemplate: any = null;

    private get dividerForScaleAdjustment() {
        // Dynamically calculate the divider based on the combined width
        // Base value of 0.2 works well for widths around 200-250
        // For larger widths, increase by 0.01 for every 50 units above 200
        const baseWidth = 200;
        const baseValue = this.dividerForScaleAdjustmentBasis;
        const increment = 0.001; // Amount to increase per step
        const incrementStep = 45; // Width step size

        if (this.combinedWidth <= baseWidth) {
            return baseValue; // Use base value for small widths
        }

        // Calculate how many steps above the base width we are
        const stepsAboveBase = Math.floor((this.combinedWidth - baseWidth) / incrementStep);

        // Calculate the new divider value
        return baseValue + stepsAboveBase * increment;
    }

    private get heightScaleFactor() {
        // Find the tallest window height
        const highestWindowHeight = Math.max(
            ...(this.productFormData?.map((row, index) => {
                if (index >= this.selectedRows.length) return 0; // Ignore unselected rows
                const heightField = row.formData.find((field) => field.fieldPID === GlobalOptions.HEIGHT_PID);
                return heightField ? parseInt(heightField.fieldValue as string) : 0;
            }) || [0]),
        );

        // For small windows (up to 1 meter), show at full size
        if (highestWindowHeight <= 100) {
            return 1.0; // 100% size
        }

        // Use logarithmic scaling for better balance with larger heights
        // Base height where scaling starts to apply
        const baseHeight = 100;

        // Calculate the scaling factor using logarithmic curve
        // This makes the scaling less aggressive for very large heights
        const logBase = 2; // Controls how quickly scaling decreases (higher = slower decrease)
        const standardLogBase = 1;
        const scaleFactor =
            Math.log((baseHeight / highestWindowHeight) * logBase + standardLogBase) /
            Math.log(logBase + standardLogBase);

        // Scale between 0.4 and 1.0 for better visibility
        const minScale = 0.4;
        const scale = minScale + scaleFactor * (standardLogBase - minScale);
        return Math.max(scale, minScale);
    }

    private get widthScaleFactor() {
        const absWidth = this.combinedWidth;
        // const absWidth = this.widthDifference > 0 ? this.totalWidth : this.combinedWidth;

        // For small widths (up to 2 meters), show at full size
        if (absWidth <= 200) {
            return 1; // 100% size
        }

        // Use logarithmic scaling for better balance with larger widths
        // Base width where scaling starts to apply
        const baseWidth = 200;

        // Calculate the scaling factor using logarithmic curve
        // This makes the scaling less aggressive for very large widths
        const logBase = 2; // Controls how quickly scaling decreases (higher = slower decrease)
        const standardLogBase = 1;
        const scaleFactor =
            Math.log((baseWidth / absWidth) * logBase + standardLogBase) / Math.log(logBase + standardLogBase);

        // Scale between 0.4 and 1.0 for better visibility
        const minScale = 0.2;
        const scale = minScale + scaleFactor * (standardLogBase - minScale);

        return Math.max(scale, minScale);
    }

    // Add a property to control the overall canvas size
    private get masterScaleFactor() {
        // Determine is width or height scale is smaller and use that as master scale
        return Math.min(this.widthScaleFactor, this.heightScaleFactor);
        // const baseWidth = 100 * this.selectedRows.length;

        // return baseWidth / this.combinedWidth;
        // return 0.7;
        // return 200 / this.combinedWidth;
    }

    // Configuration for the layer to move it down and add padding
    private get layerConfig() {
        // Calculate centering offsets to keep canvas centered in the container
        return {
            y: 30, // Add top padding
            x: 30, // Add left padding to center
        };
    }

    private windowTemplates: WindowTemplate[] = [
        {
            id: 'template-2',
            stageConfig: { height: 0, width: 0, x: 50, y: 0 },
            windowElements: [
                {
                    id: 'window-1',
                    canvasConfig: {
                        width: 0,
                        height: 0,
                        positionX: null,
                        positionY: null,
                        initialPositionX: null,
                        initialPositionY: 35,
                        position: 1,
                        guideType: '',
                    },
                },
                {
                    id: 'window-2',
                    canvasConfig: {
                        width: 0,
                        height: 0,
                        positionX: null,
                        positionY: null,
                        initialPositionX: null,
                        initialPositionY: 35,
                        position: 2,
                        guideType: '',
                    },
                },
                {
                    id: 'window-3',
                    canvasConfig: {
                        width: 0,
                        height: 0,
                        positionX: null,
                        positionY: null,
                        initialPositionX: null,
                        initialPositionY: 35,
                        position: 3,
                        guideType: '',
                    },
                },
            ],
        },
    ];

    private setTemplatesPositionsAndStageConfig() {
        this.windowTemplates.forEach(async (windowTemplate: WindowTemplate) => {
            this.setTemplatePositionsAndStageConfig(windowTemplate);
        });
    }

    private async setTemplatePositionsAndStageConfig(windowTemplate: WindowTemplate, setStage: boolean = true) {
        const dividerForStageWidth = 4.5 + (windowTemplate.windowElements.length * this.percentageIncreasement) / 10;
        await this.$nextTick();

        const { width, height } = windowTemplate.windowElements.reduce(
            (total: any, currentElement: any) => {
                total.width += parseFloat(this.transformDimension(currentElement.canvasConfig.width) as any);
                total.height += parseFloat(this.transformDimension(currentElement.canvasConfig.height) as any);

                return total;
            },
            { width: 0, height: 0 },
        );

        const stageConfig = {
            height: this.calculateStageConfigHeight(windowTemplate), // Use
            // lower total width for stage since we scaled it down in canvas
            width:
                (width * this.masterScaleFactor + this.extraSpaceForLabels) / dividerForStageWidth +
                this.widthDifference * (this.masterScaleFactor + this.extraWidthPaddingScale),
            scale: { x: this.masterScaleFactor, y: this.masterScaleFactor }, // Apply scaling to entire stage
            x: this.widthDifference / 2, // Start with some left padding in case of extra width
        };

        if (setStage) {
            // Set state config
            this.$set(windowTemplate, 'stageConfig', { ...stageConfig });
        }

        // Set initial positions in a template
        this.setElementsInitialPositions(windowTemplate.windowElements);
    }

    private setElementsInitialPositions(windowElements: WindowElement[]) {
        // Sort elements by position before setting initial positions
        const sortedElements = [...windowElements].sort((a, b) => {
            const positionA =
                typeof a.canvasConfig.position === 'string'
                    ? parseInt(a.canvasConfig.position, 10)
                    : a.canvasConfig.position || 0;
            const positionB =
                typeof b.canvasConfig.position === 'string'
                    ? parseInt(b.canvasConfig.position, 10)
                    : b.canvasConfig.position || 0;

            return positionA - positionB;
        });

        // We set initial position coordinates based on the following logic:
        // Each element has its position equal to the previous element position plus
        // The previous element width/height / divisor (higher number = smaller gap between windows)

        sortedElements.forEach((element: any, i: number) => {
            if (i === 0) {
                this.$set(element.canvasConfig, 'initialPositionX', 50);
                // Also update the positionX value
                this.$set(element.canvasConfig, 'positionX', 50);
            } else {
                const transformedWidth = this.transformDimension(sortedElements[i - 1]?.canvasConfig.width) as any;
                const transformedXPosition = sortedElements[i - 1]?.canvasConfig.initialPositionX || 0;
                const newPositionX =
                    (transformedWidth - this.increasementForWiderWidths(transformedWidth)) *
                        this.dividerForScaleAdjustment +
                    transformedXPosition;

                // Set both initialPositionX and positionX
                this.$set(element.canvasConfig, 'initialPositionX', newPositionX);
                this.$set(element.canvasConfig, 'positionX', newPositionX);
            }

            // Also update positionY to match initialPositionY
            this.$set(element.canvasConfig, 'positionY', element.canvasConfig.initialPositionY);
        });
    }

    private increasementForWiderWidths(width: string) {
        const medianValue = 250;
        const dividerForUpscaling = 6.76;

        if (+width <= medianValue) {
            return 0;
        }

        const baseDifference = parseFloat(width) - medianValue;

        return Math.round(baseDifference / dividerForUpscaling);
    }

    private calculateStageConfigHeight(template: WindowTemplate) {
        const height = Math.max(
            ...template.windowElements.map((element) => this.transformDimension(element.canvasConfig.height) as number),
        );
        const scaleFactor = this.masterScaleFactor === 1 ? 1 : this.masterScaleFactor - 0.1;

        // Add extra margin to accommodate elements above and below the window
        // Increase margin to ensure all elements (circles + guides + window) are visible
        const extraMargin = this.dynamicCanvasSizesBasedOnProduct.extraMarginForCanvasStage; // Additional space for circle elements, guides, and space below
        // Use a bigger divisor to make the canvas even smaller, fitting more content on screen
        const heightDivisor = 3.5 / scaleFactor; // Increasing this value makes the canvas shorter

        return (height + extraMargin) / heightDivisor;
    }

    private transformDimension(value: string | number) {
        // Since we're only using CM, we multiply it by 10
        return parseFloat(value as any) * 10;

        /* Original code with MM support
        if (this.metricUnit === 'mm') {
            return parseFloat(value as any);
        } else if (this.metricUnit === 'cm') {
            return parseFloat(value as any) * 10;
        }
        */
    }

    // Method to provide circle letters configuration for each window
    private getCircleLetters(windowElement: WindowElement, index: number): Array<{ letter: string; visible: boolean }> {
        // Default configuration - can be extended based on your requirements

        const drive = this.productFormData?.[index]?.formData.find(
            (field) => field.fieldPID === GlobalOptions.DRIVE_PID,
        )?.fieldValue;

        const defaultConfig = [
            { letter: drive?.toString() ?? '', visible: !!drive },
            // { letter: 'D', visible: false }, // Show 'D' only for even index windows
        ];

        // This can be expanded to dynamically determine visibility based on various conditions
        // For example, you could use data from productFormData, windowElement properties, etc.

        return defaultConfig;
    }

    private selectWindowTemplate(templateId: string) {
        if (templateId === '') {
            this.selectedTemplate = null;
            return;
        }

        this.selectedTemplate = JSON.parse(
            JSON.stringify(this.windowTemplates.find((template: any) => template.id === templateId)),
        );
    }

    private calculateDynamicWidth(fieldValue: string, totalWidth: number): number {
        const parsedFieldValue = parseInt(fieldValue);
        const totalWidthsOfSelectedWindows =
            this.productFormData?.reduce((total, row) => {
                const widthField = row.formData.find((field) => field.fieldPID === GlobalOptions.WIDTH_PID);
                return total + (widthField ? parseInt(widthField.fieldValue as string) : 0);
            }, 0) || 0;

        const differenceBetweenTotalAndSelectedWidth = totalWidth - totalWidthsOfSelectedWindows;

        if (differenceBetweenTotalAndSelectedWidth > 0) {
            return parsedFieldValue + differenceBetweenTotalAndSelectedWidth / this.selectedRows.length;
        }

        return parsedFieldValue;
    }

    private setSelectedTemplateDimensions() {
        if (!this.selectedTemplate) return;
        this.productFormData?.forEach((row: CombinationsConfiguratorFormData, index: number) => {
            row.formData.forEach((field: any) => {
                switch (field.fieldPID) {
                    case GlobalOptions.WIDTH_PID:
                        this.selectedTemplate.windowElements[index].canvasConfig.width = parseInt(
                            field.fieldValue as string,
                        );
                        break;
                    case GlobalOptions.HEIGHT_PID:
                        this.selectedTemplate.windowElements[index].canvasConfig.height = parseInt(
                            field.fieldValue as string,
                        );
                        break;
                    case GlobalOptions.ASSEMBLY_POSITION_PID:
                        // Update the position property
                        this.selectedTemplate.windowElements[index].canvasConfig.position = field.fieldValue as string;

                        // When position changes, we need to recalculate all positionX and positionY values
                        // We'll set them to null so they get recalculated in setElementsInitialPositions
                        this.selectedTemplate.windowElements.forEach((element: any) => {
                            element.canvasConfig.positionX = null;
                            element.canvasConfig.positionY = null;
                            element.canvasConfig.initialPositionX = null;
                        });
                        break;
                }
            });
        });

        // Reset window templates that are not selected
        for (let i = this.selectedRows.length; i < this.selectedTemplate.windowElements.length; i++) {
            this.selectedTemplate.windowElements[i].canvasConfig.width = 0;
            this.selectedTemplate.windowElements[i].canvasConfig.height = 0;
            this.selectedTemplate.windowElements[i].canvasConfig.positionX = null;
            this.selectedTemplate.windowElements[i].canvasConfig.positionY = null;
            this.selectedTemplate.windowElements[i].canvasConfig.initialPositionX = null;
            this.selectedTemplate.windowElements[i].canvasConfig.initialPositionY = 35;
            this.selectedTemplate.windowElements[i].canvasConfig.position = i + 1;
        }
    }

    private moveWindowsToTop() {
        setTimeout(() => {
            // Create a sorted array of window elements by position
            const sortedWindows = [...this.selectedTemplate.windowElements].sort((a, b) => {
                const positionA =
                    typeof a.canvasConfig.position === 'string'
                        ? parseInt(a.canvasConfig.position, 10)
                        : a.canvasConfig.position || 0;
                const positionB =
                    typeof b.canvasConfig.position === 'string'
                        ? parseInt(b.canvasConfig.position, 10)
                        : b.canvasConfig.position || 0;

                return positionA - positionB;
            });

            // Move each window to top in the proper order
            for (let i = sortedWindows.length - 1; i >= 0; i--) {
                const window = sortedWindows[i];
                // Use the exposed method from child component
                const windowComponent = this.$refs[window.id];
                if (windowComponent && Array.isArray(windowComponent) && windowComponent[0]) {
                    // Vue gives refs to v-for components as arrays
                    (windowComponent[0] as any).moveWindowToTop();

                    // Make sure positionX/Y are synchronized with initialPositionX/Y
                    window.canvasConfig.positionX = window.canvasConfig.initialPositionX;
                    window.canvasConfig.positionY = window.canvasConfig.initialPositionY;
                }
            }
        }, 0);
    }

    private async recalculateCanvas() {
        this.setSelectedTemplateDimensions();
        await this.$nextTick();

        // Update both stage config and positions
        this.setTemplatePositionsAndStageConfig(this.selectedTemplate);

        await this.$nextTick();
        // For ordering the windows by z-index and make the former stack first
        this.moveWindowsToTop();
    }

    private async mounted() {
        this.setTemplatesPositionsAndStageConfig();
        await this.$nextTick();
        this.selectWindowTemplate('template-2');
        await this.$nextTick();
        await this.recalculateCanvas();
    }

    @Watch('productFormData', { immediate: false, deep: true })
    @Watch('guideType', { immediate: false })
    @Watch('secondGuideType', { immediate: false })
    private async onProductFormDataChange() {
        if (!this.productFormData) return;
        await this.recalculateCanvas();

        // Force re-render by incrementing the renderKey
        // this.renderKey++;
    }

    @Watch('widthDifference', { immediate: false })
    private async onTotalWidthChange() {
        await this.recalculateCanvas();
    }

    // Computed property to get sorted window elements by position
    private get sortedWindowElements() {
        if (!this.selectedTemplate || !this.selectedTemplate.windowElements) return [];

        const filteredElements = this.selectedTemplate.windowElements.filter((element: any, index: number) => {
            // Only include elements that correspond to selected rows
            return index < this.selectedRows.length;
        });

        return [...filteredElements].sort((a, b) => {
            const positionA =
                typeof a.canvasConfig.position === 'string'
                    ? parseInt(a.canvasConfig.position, 10)
                    : a.canvasConfig.position || 0;
            const positionB =
                typeof b.canvasConfig.position === 'string'
                    ? parseInt(b.canvasConfig.position, 10)
                    : b.canvasConfig.position || 0;

            return positionA - positionB;
        });
    }
}
